/**
 * R1AS faucet click → blue water — docs/R1AS_FAUCET_EASTER_EGG.md
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const WATER = '#4aa8ff';
const WATER_SOFT = '#7ec4ff';

/**
 * Near-camera depth override — SoftPlan basins/counters sit in front of the
 * spout and depth-occlude the water at exactly the range where the user is
 * watching it. Within NEAR_ON meters the FX draws on top (depthTest off);
 * beyond NEAR_OFF it depth-tests again so walls occlude from outside.
 * Hysteresis avoids flicker at the boundary. docs/R1AS_FAUCET_EASTER_EGG.md
 */
const DEPTH_NEAR_ON_M = 3.0;
const DEPTH_NEAR_OFF_M = 3.8;

function applyNearDepth(nearRef, camera, origin, materials) {
  const dist = camera.position.distanceTo(origin);
  if (nearRef.current) {
    if (dist > DEPTH_NEAR_OFF_M) nearRef.current = false;
  } else if (dist < DEPTH_NEAR_ON_M) {
    nearRef.current = true;
  }
  const depthTest = !nearRef.current;
  for (const mat of materials) {
    if (mat && mat.depthTest !== depthTest) mat.depthTest = depthTest;
  }
}

const _v = new THREE.Vector3();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const _ab = new THREE.Vector3();
const _ac = new THREE.Vector3();
const _n = new THREE.Vector3();
const _cent = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _s = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _sc = new THREE.Vector3();

/**
 * Area-weighted center of downward-facing triangles on the bottom of the mesh.
 * Falls back to average of vertices in the bottom Y band.
 * @param {THREE.BufferGeometry} geometry
 * @param {THREE.Matrix4} matrixWorld
 * @param {{ downDot?: number, yBand?: number }} [opts]
 * @returns {THREE.Vector3 | null}
 */
export function findBottomFaceCenter(geometry, matrixWorld, opts = {}) {
  if (!geometry?.attributes?.position || !matrixWorld) return null;
  const downDot = opts.downDot ?? -0.55;
  const yBand = opts.yBand ?? 0.04;
  const pos = geometry.attributes.position;
  const idx = geometry.index;
  const triCount = idx ? idx.count / 3 : pos.count / 3;
  if (!triCount) return null;

  let minY = Infinity;
  for (let i = 0; i < pos.count; i += 1) {
    _v.fromBufferAttribute(pos, i).applyMatrix4(matrixWorld);
    if (_v.y < minY) minY = _v.y;
  }

  const getCorner = (t, k) => (idx ? idx.getX(t * 3 + k) : t * 3 + k);

  let sx = 0;
  let sy = 0;
  let sz = 0;
  let areaSum = 0;
  let nTris = 0;

  for (let t = 0; t < triCount; t += 1) {
    _a.fromBufferAttribute(pos, getCorner(t, 0)).applyMatrix4(matrixWorld);
    _b.fromBufferAttribute(pos, getCorner(t, 1)).applyMatrix4(matrixWorld);
    _c.fromBufferAttribute(pos, getCorner(t, 2)).applyMatrix4(matrixWorld);
    _ab.subVectors(_b, _a);
    _ac.subVectors(_c, _a);
    _n.crossVectors(_ab, _ac);
    const area = _n.length() * 0.5;
    if (area < 1e-12) continue;
    _n.normalize();
    if (_n.y > downDot) continue; // not facing down
    _cent.set(
      (_a.x + _b.x + _c.x) / 3,
      (_a.y + _b.y + _c.y) / 3,
      (_a.z + _b.z + _c.z) / 3
    );
    if (_cent.y > minY + yBand) continue;
    sx += _cent.x * area;
    sy += _cent.y * area;
    sz += _cent.z * area;
    areaSum += area;
    nTris += 1;
  }

  if (areaSum > 0 && nTris > 0) {
    return new THREE.Vector3(sx / areaSum, sy / areaSum, sz / areaSum);
  }

  // Fallback: bottom vertex band centroid
  const band = 0.012;
  sx = sy = sz = 0;
  let nv = 0;
  for (let i = 0; i < pos.count; i += 1) {
    _v.fromBufferAttribute(pos, i).applyMatrix4(matrixWorld);
    if (_v.y > minY + band) continue;
    sx += _v.x;
    sy += _v.y;
    sz += _v.z;
    nv += 1;
  }
  if (!nv) return null;
  return new THREE.Vector3(sx / nv, sy / nv, sz / nv);
}

/** World matrix matching LabViewer: parent inch→m scale × mesh local pose. */
export function spoutWorldMatrix(pose, parentScale = 1 / 39.37) {
  _p.fromArray(pose.position);
  _q.fromArray(pose.quaternion);
  _sc.fromArray(pose.scale);
  _m.compose(_p, _q, _sc);
  _s.makeScale(parentScale, parentScale, parentScale);
  return _s.clone().multiply(_m);
}

/**
 * @param {{
 *   min: number[], max: number[], center: number[]
 * }} spoutBounds
 * @param {object} station
 */
export function resolveSpoutOrigin(spoutBounds, station) {
  if (!spoutBounds?.min || !spoutBounds?.max || !spoutBounds?.center) {
    return null;
  }
  const [minx, miny, minz] = spoutBounds.min;
  const [maxx, , maxz] = spoutBounds.max;
  const [cx, , cz] = spoutBounds.center;
  const mount = station.mount;

  let x = cx;
  let z = cz;
  let y = miny + 0.006;

  if (mount === '-x') x = maxx - 0.02;
  else if (mount === '+x') x = minx + 0.02;
  else if (mount === '-z') z = maxz - 0.02;
  else if (mount === '+z') z = minz + 0.02;

  const nudge = station.spoutNudge || [0, 0, 0];
  return new THREE.Vector3(x + nudge[0], y + nudge[1], z + nudge[2]);
}

/**
 * Raycast ONLY faucet hit meshes so basin/counter (Bvh firstHitOnly) can't steal clicks.
 */
export function FaucetPointerBridge({
  enabled = false,
  /** When true, clicks are reserved for annotate placement */
  suppress = false,
  targetsRef,
  onToggle,
}) {
  const { camera, gl } = useThree();
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  const downRef = useRef(null);
  const hoverIdRef = useRef(null);

  useEffect(() => {
    if (!enabled || suppress) {
      gl.domElement.style.cursor = suppress ? 'crosshair' : '';
      return undefined;
    }

    const el = gl.domElement;

    const pick = (clientX, clientY) => {
      const targets = targetsRef?.current;
      if (!targets?.length) return null;
      const rect = el.getBoundingClientRect();
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(targets, false);
      if (!hits.length) return null;
      return hits[0].object.userData?.faucetStationId || null;
    };

    const onDown = (e) => {
      if (e.button !== 0) return;
      downRef.current = { x: e.clientX, y: e.clientY, id: pick(e.clientX, e.clientY) };
    };

    const onMove = (e) => {
      const id = pick(e.clientX, e.clientY);
      if (id !== hoverIdRef.current) {
        hoverIdRef.current = id;
        el.style.cursor = id ? 'pointer' : '';
      }
    };

    const onUp = (e) => {
      const down = downRef.current;
      downRef.current = null;
      if (!down || e.button !== 0) return;
      const dx = e.clientX - down.x;
      const dy = e.clientY - down.y;
      if (dx * dx + dy * dy > 64) return; // drag ≠ click
      const id = pick(e.clientX, e.clientY) || down.id;
      if (id) onToggle?.(id);
    };

    const onLeave = () => {
      hoverIdRef.current = null;
      el.style.cursor = '';
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointerleave', onLeave);
      el.style.cursor = '';
    };
  }, [enabled, suppress, camera, gl, onToggle, raycaster, ndc, targetsRef]);

  return null;
}

/**
 * @param {{
 *   station: object,
 *   spoutBounds: { min: number[], max: number[], center: number[] } | null,
 *   spoutGeometry?: THREE.BufferGeometry | null,
 *   spoutPose?: { position: number[], quaternion: number[], scale: number[] } | null,
 *   parentScale?: number,
 *   active: boolean,
 * }} props
 */
export function FaucetWaterFx({
  station,
  spoutBounds,
  spoutGeometry = null,
  spoutPose = null,
  /** Authored pin [x,y,z] wins over mesh geometry */
  spoutOverride = null,
  parentScale = 1 / 39.37,
  active,
}) {
  const origin = useMemo(() => {
    if (Array.isArray(spoutOverride) && spoutOverride.length >= 3) {
      const nudge = station.spoutNudge || [0, 0, 0];
      return new THREE.Vector3(
        spoutOverride[0] + nudge[0],
        spoutOverride[1] + nudge[1],
        spoutOverride[2] + nudge[2]
      );
    }
    if (spoutGeometry && spoutPose) {
      const mw = spoutWorldMatrix(spoutPose, parentScale);
      const face = findBottomFaceCenter(spoutGeometry, mw, {
        // Shower head disks are flatter; sink tips are smaller / noisier
        downDot: station.type === 'shower' ? -0.7 : -0.45,
        yBand: station.type === 'shower' ? 0.03 : 0.05,
      });
      if (face) {
        const nudge = station.spoutNudge || [0, 0, 0];
        face.x += nudge[0];
        face.y += nudge[1];
        face.z += nudge[2];
        return face;
      }
    }
    return resolveSpoutOrigin(spoutBounds, station);
  }, [
    station,
    spoutBounds,
    spoutGeometry,
    spoutPose,
    spoutOverride,
    parentScale,
  ]);

  if (!active || !origin) return null;

  if (station.type === 'shower') {
    return <ShowerSpray origin={origin} />;
  }
  return <SinkStream origin={origin} />;
}

function SinkStream({ origin }) {
  const count = 40;
  const timeRef = useRef(0);
  const originRef = useRef(origin);
  originRef.current = origin;
  const nearRef = useRef(false);
  const streamMatRef = useRef(null);
  const dripsMatRef = useRef(null);
  const seeds = useMemo(
    () => Float32Array.from({ length: count }, () => Math.random()),
    [count]
  );
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const attr = new THREE.BufferAttribute(new Float32Array(count * 3), 3);
    attr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', attr);
    return geo;
  }, [count]);

  useFrame((state, dt) => {
    timeRef.current += Math.min(dt, 0.05);
    const t = timeRef.current;
    const o = originRef.current;
    if (!o) return;

    // Basin/counter meshes must not hide the stream while standing at the sink.
    applyNearDepth(nearRef, state.camera, o, [
      streamMatRef.current,
      dripsMatRef.current,
    ]);

    const attr = geometry.getAttribute('position');
    if (!attr) return;
    const arr = attr.array;
    for (let i = 0; i < count; i += 1) {
      const life = (t * 2.1 + seeds[i]) % 1;
      const fall = life * 0.38;
      const jitter = (seeds[i] - 0.5) * 0.008 * life;
      arr[i * 3] = o.x + jitter;
      arr[i * 3 + 1] = o.y - fall;
      arr[i * 3 + 2] = o.z + jitter * 0.5;
    }
    attr.needsUpdate = true;
  });

  const streamLen = 0.34;
  return (
    <group>
      <mesh
        position={[origin.x, origin.y - streamLen * 0.5, origin.z]}
        renderOrder={6}
      >
        <cylinderGeometry args={[0.006, 0.01, streamLen, 8]} />
        <meshBasicMaterial
          ref={streamMatRef}
          color={WATER}
          transparent
          opacity={0.62}
          depthWrite={false}
        />
      </mesh>
      <points geometry={geometry} renderOrder={7}>
        <pointsMaterial
          ref={dripsMatRef}
          color={WATER_SOFT}
          size={0.022}
          transparent
          opacity={0.9}
          depthWrite={false}
          sizeAttenuation
        />
      </points>
    </group>
  );
}

/** Fall length / cone spread — docs/R1AS_FAUCET_EASTER_EGG.md */
const SHOWER_FALL = 1.05;
const SHOWER_SPREAD = 0.18;

function ShowerSpray({ origin }) {
  const dropletCount = 160;
  const streakCount = 56;
  const timeRef = useRef(0);
  const originRef = useRef(origin);
  originRef.current = origin;
  const nearRef = useRef(false);
  const streakMatRef = useRef(null);
  const dropMatRef = useRef(null);

  const dropletSeeds = useMemo(() => {
    const out = [];
    for (let i = 0; i < dropletCount; i += 1) {
      out.push({
        a: Math.random() * Math.PI * 2,
        r: 0.008 + Math.random() * SHOWER_SPREAD,
        speed: 1.15 + Math.random() * 0.95,
        phase: Math.random(),
      });
    }
    return out;
  }, [dropletCount]);

  const streakSeeds = useMemo(() => {
    const out = [];
    for (let i = 0; i < streakCount; i += 1) {
      out.push({
        a: Math.random() * Math.PI * 2,
        r: 0.012 + Math.random() * (SHOWER_SPREAD * 0.9),
        speed: 0.95 + Math.random() * 0.65,
        phase: Math.random(),
        len: 0.08 + Math.random() * 0.14,
      });
    }
    return out;
  }, [streakCount]);

  const dropletGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const attr = new THREE.BufferAttribute(new Float32Array(dropletCount * 3), 3);
    attr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', attr);
    return geo;
  }, [dropletCount]);

  const streakGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const attr = new THREE.BufferAttribute(
      new Float32Array(streakCount * 2 * 3),
      3
    );
    attr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', attr);
    return geo;
  }, [streakCount]);

  const pointsRef = useRef(null);
  const linesRef = useRef(null);

  useFrame((state, dt) => {
    // Local clock — keeps running even if r3f clock stalls after long walks
    timeRef.current += Math.min(dt, 0.05);
    const t = timeRef.current;
    const o = originRef.current;
    if (!o) return;

    // Shower glass / basin lips must not hide spray at watching distance.
    applyNearDepth(nearRef, state.camera, o, [
      streakMatRef.current,
      dropMatRef.current,
    ]);

    const dAttr = dropletGeo.getAttribute('position');
    if (dAttr) {
      const arr = dAttr.array;
      for (let i = 0; i < dropletCount; i += 1) {
        const s = dropletSeeds[i];
        const life = (t * s.speed + s.phase) % 1;
        const fall = life * SHOWER_FALL;
        const spread = s.r * (0.2 + life * 1.4);
        arr[i * 3] = o.x + Math.cos(s.a) * spread;
        arr[i * 3 + 1] = o.y - fall;
        arr[i * 3 + 2] = o.z + Math.sin(s.a) * spread;
      }
      dAttr.needsUpdate = true;
    }

    const sAttr = streakGeo.getAttribute('position');
    if (sAttr) {
      const arr = sAttr.array;
      for (let i = 0; i < streakCount; i += 1) {
        const s = streakSeeds[i];
        const life = (t * s.speed + s.phase) % 1;
        const fall = life * SHOWER_FALL;
        const spread = s.r * (0.2 + life * 1.45);
        const cx = o.x + Math.cos(s.a) * spread;
        const cz = o.z + Math.sin(s.a) * spread;
        const y0 = o.y - fall;
        const y1 = y0 - s.len * (0.45 + life * 0.7);
        const base = i * 6;
        arr[base] = cx;
        arr[base + 1] = y0;
        arr[base + 2] = cz;
        arr[base + 3] = cx;
        arr[base + 4] = y1;
        arr[base + 5] = cz;
      }
      sAttr.needsUpdate = true;
    }

    // Keep drawables alive when briefly culled (walk away / back)
    if (pointsRef.current) pointsRef.current.frustumCulled = false;
    if (linesRef.current) linesRef.current.frustumCulled = false;
  });

  return (
    <group>
      <lineSegments ref={linesRef} geometry={streakGeo} renderOrder={6} frustumCulled={false}>
        <lineBasicMaterial
          ref={streakMatRef}
          color={WATER}
          transparent
          opacity={0.55}
          depthWrite={false}
          depthTest
        />
      </lineSegments>

      <points ref={pointsRef} geometry={dropletGeo} renderOrder={7} frustumCulled={false}>
        <pointsMaterial
          ref={dropMatRef}
          color={WATER}
          size={0.036}
          transparent
          opacity={0.88}
          depthWrite={false}
          depthTest
          sizeAttenuation
        />
      </points>
    </group>
  );
}
